/**
 * OrgUnitsTab — zakładka „Struktura" (jednostki organizacyjne), F24 Pakiet 0.
 *
 * Rename + rewrite `DepartmentsTab` (T9.4, dawniej `CompanyDetailDrawer.departments.tsx`):
 * endpoint `/departments` → `/org-units`, drzewo teraz niesie TRZY rodzaje jednostki
 * (dział/pion/oddział), kod MPK i kierownika (rekord kadrowy, nie konto logowania).
 *
 * Rewrite z react-arborist na własne, NIEWIRTUALIZOWANE drzewo (rekurencyjne `<ul>`,
 * expand/collapse w lokalnym `Set`) — react-arborist wymagał STAŁEGO `rowHeight` (32px)
 * i fixed width/height kontenera; wiersz niosący teraz nazwę + kod MPK + badge rodzaju +
 * kierownika + 4 akcje nie mieści się w 32px (przycinanie/nakładanie treści), a recon
 * zgłosił renderowanie drzewa jako niewiarygodne. Własne drzewo renderuje CAŁĄ odpowiedź
 * listy (bez wirtualizacji, bez przycinania wysokości wiersza) i domyślnie ma wszystko
 * rozwinięte.
 *
 * Źródło danych: WYŁĄCZNIE płaska lista `GET /org-units` (`useOrgUnitsForCompany`) —
 * drzewo budowane po stronie klienta z `parentId` (mapa `unitsByParent`). Endpoint
 * `/org-units/tree` (osobny hook `useOrgUnitsTree`) zostaje dla organigramu
 * (`OrgChartCanvas`/`OrgChart.tsx`) — jego kształt (`managerName` już rozwiązane,
 * bez `code`/`isActive`) nie wystarcza do formularza edycji tej zakładki.
 *
 * Reorder: strzałki góra/dół (zamiast drag&drop react-arborist) — wołają to samo
 * `POST /reorder` co dawniej, bez zależności od biblioteki DnD; czytelniejsze i
 * testowalne przez RTL (klik zamiast symulacji drag). Odstępstwo od „reorder jak
 * dziś" — świadome, uzasadnienie w raporcie zadania.
 *
 * Operacje:
 *  - Dodaj root / dodaj podjednostkę (modal: nazwa, kod MPK, rodzaj, kierownik, aktywna).
 *  - Edytuj (ten sam modal, prefilled) — w tym PRZENIESIENIE (picker nadrzędnej jednostki
 *    w modalu, PATCH `parentId`; cykle/firma waliduje backend — `CYCLE_DETECTED` /
 *    `PARENT_NOT_IN_COMPANY`).
 *  - Usuń (`ConfirmDialog` danger) — 409 `ORG_UNIT_HAS_CHILDREN`/`ORG_UNIT_HAS_EMPLOYEES` → toast.
 *  - Strzałki góra/dół — `POST /reorder` w obrębie tego samego rodzica.
 *
 * Backend: GET/POST/PATCH/DELETE `/companies/:companyId/org-units[/:id]`, POST `/reorder`.
 */

import { type FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ChevronDown, ChevronRight, MoveDown, MoveUp, Pencil, Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import {
  buildIndentedOrgUnitOptions,
  ORG_UNIT_KINDS,
  ORG_UNITS_BY_COMPANY_KEY,
  type OrgUnitItem,
  type OrgUnitKind,
  useEmployeesList,
  useOrgUnitsForCompany,
} from '@/lib/use-tenant-people';

/** `Badge` `tone` (nie `color` — paleta `color` nie ma fioletu). */
const KIND_BADGE_TONE: Record<OrgUnitKind, 'info' | 'purple' | 'warning'> = {
  department: 'info',
  division: 'purple',
  branch: 'warning',
};

interface UnitFormState {
  name: string;
  code: string;
  kind: OrgUnitKind;
  managerEmployeeId: string;
  parentId: string;
  isActive: boolean;
}

function emptyForm(parentId: string | null): UnitFormState {
  return {
    name: '',
    code: '',
    kind: 'department',
    managerEmployeeId: '',
    parentId: parentId ?? '',
    isActive: true,
  };
}

function unitToForm(unit: OrgUnitItem): UnitFormState {
  return {
    name: unit.name,
    code: unit.code ?? '',
    kind: unit.kind,
    managerEmployeeId: unit.managerEmployeeId ?? '',
    parentId: unit.parentId ?? '',
    isActive: unit.isActive,
  };
}

type ModalState = { kind: 'create'; parentId: string | null } | { kind: 'edit'; unit: OrgUnitItem };

export function OrgUnitsTab({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const unitsQ = useOrgUnitsForCompany(companyId);
  const units = unitsQ.data?.orgUnits ?? [];
  const managersQ = useEmployeesList({ companyId, isActive: true, limit: 200 });
  const managers = managersQ.data?.employees ?? [];

  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<ModalState | null>(null);
  const [form, setForm] = useState<UnitFormState>(emptyForm(null));
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const unitsByParent = useMemo(() => {
    const map = new Map<string | null, OrgUnitItem[]>();
    for (const u of units) {
      const list = map.get(u.parentId) ?? [];
      list.push(u);
      map.set(u.parentId, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
        const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
        if (ao !== bo) return ao - bo;
        return a.name.localeCompare(b.name);
      });
    }
    return map;
  }, [units]);

  const roots = unitsByParent.get(null) ?? [];

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ORG_UNITS_BY_COMPANY_KEY(companyId) });
  }

  function mapMutationError(err: unknown, fallbackKey: string): string {
    const code = isAxiosError(err) ? (err.response?.data as { error?: string })?.error : undefined;
    if (code) {
      const msg = t(`tenant.orgUnits.toast.errors.${code}`, { defaultValue: '' });
      if (msg) return msg;
    }
    return t(fallbackKey);
  }

  const saveMutation = useMutation({
    mutationFn: async (vars: { editingId: string | null; body: Record<string, unknown> }) => {
      if (vars.editingId) {
        const res = await api.patch(
          `/companies/${companyId}/org-units/${vars.editingId}`,
          vars.body,
        );
        return res.data;
      }
      const res = await api.post(`/companies/${companyId}/org-units`, vars.body);
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.show({
        variant: 'success',
        title: t(
          modal?.kind === 'edit'
            ? 'tenant.orgUnits.toast.updated'
            : 'tenant.orgUnits.toast.created',
        ),
      });
      setModal(null);
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: mapMutationError(err, 'tenant.orgUnits.toast.saveError'),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      setPendingDeleteId(id);
      try {
        const res = await api.delete(`/companies/${companyId}/org-units/${id}`);
        return res.data;
      } finally {
        setPendingDeleteId(null);
      }
    },
    onSuccess: () => {
      invalidate();
      toast.show({ variant: 'success', title: t('tenant.orgUnits.toast.deleted') });
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: mapMutationError(err, 'tenant.orgUnits.toast.deleteError'),
      });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (vars: {
      orgUnitId: string;
      newParentId: string | null;
      targetIndex: number;
    }) => {
      const res = await api.post(`/companies/${companyId}/org-units/reorder`, vars);
      return res.data;
    },
    onSuccess: () => invalidate(),
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: mapMutationError(err, 'tenant.orgUnits.toast.reorderError'),
      });
    },
  });

  function toggleCollapsed(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function openCreate(parentId: string | null) {
    setForm(emptyForm(parentId));
    setModal({ kind: 'create', parentId });
  }

  function openEdit(unit: OrgUnitItem) {
    setForm(unitToForm(unit));
    setModal({ kind: 'edit', unit });
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) return;
    const body: Record<string, unknown> = {
      name,
      code: form.code.trim() || null,
      kind: form.kind,
      managerEmployeeId: form.managerEmployeeId || null,
      isActive: form.isActive,
    };
    if (modal?.kind === 'edit') {
      // Rodzic edytowalny WYŁĄCZNIE w trybie edit (create ma go już ustalony przez
      // kontekst kliknięcia „+"/„Dodaj jednostkę") — zmiana = PRZENIESIENIE (PATCH parentId).
      body.parentId = form.parentId || null;
      saveMutation.mutate({ editingId: modal.unit.id, body });
    } else {
      body.parentId = modal?.parentId ?? null;
      saveMutation.mutate({ editingId: null, body });
    }
  }

  function handleMove(unit: OrgUnitItem, direction: -1 | 1) {
    const siblings = unitsByParent.get(unit.parentId) ?? [];
    const idx = siblings.findIndex((s) => s.id === unit.id);
    const targetIdx = idx + direction;
    if (idx === -1 || targetIdx < 0 || targetIdx >= siblings.length) return;
    reorderMutation.mutate({
      orgUnitId: unit.id,
      newParentId: unit.parentId,
      targetIndex: targetIdx,
    });
  }

  if (unitsQ.isError) {
    return <p className="py-4 text-sm text-red-600">{t('tenant.orgUnits.error')}</p>;
  }

  const parentPickerOptions =
    modal?.kind === 'edit' ? buildIndentedOrgUnitOptions(units, modal.unit.id) : [];

  return (
    <div className="space-y-3" data-testid="org-units-tab">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-text-primary">{t('tenant.orgUnits.title')}</span>
        <IconButton
          variant="subtle"
          size="sm"
          aria-label={t('tenant.orgUnits.addRootBtn')}
          onClick={() => openCreate(null)}
          data-testid="org-units-add-root-btn"
        >
          <Plus aria-hidden="true" />
        </IconButton>
      </div>

      {roots.length === 0 && !unitsQ.isLoading ? (
        <p className="py-4 text-center text-sm text-text-tertiary">{t('tenant.orgUnits.empty')}</p>
      ) : (
        <ul
          className="space-y-0.5 rounded-lg border border-border-secondary p-2"
          data-testid="org-units-tree"
        >
          {roots.map((unit, rootIdx) => (
            <OrgUnitNode
              key={unit.id}
              unit={unit}
              depth={0}
              unitsByParent={unitsByParent}
              collapsed={collapsed}
              onToggleCollapsed={toggleCollapsed}
              onAddChild={openCreate}
              onEdit={openEdit}
              onDelete={(id) => setConfirmDeleteId(id)}
              onMove={handleMove}
              pendingDeleteId={pendingDeleteId}
              siblingsLength={roots.length}
              index={rootIdx}
            />
          ))}
        </ul>
      )}

      <Modal
        open={modal !== null}
        onClose={() => setModal(null)}
        title={
          modal?.kind === 'edit'
            ? t('tenant.orgUnits.modal.editTitle')
            : modal?.parentId
              ? t('tenant.orgUnits.modal.newChildTitle')
              : t('tenant.orgUnits.modal.newRootTitle')
        }
        footer={
          <>
            <Button type="button" variant="ghost" size="sm" onClick={() => setModal(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="org-unit-form"
              variant="primary"
              size="sm"
              disabled={saveMutation.isPending || !form.name.trim()}
              data-testid="org-unit-form-submit"
            >
              {saveMutation.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </>
        }
      >
        <form id="org-unit-form" onSubmit={handleSubmit} className="space-y-4">
          <Input
            label={t('tenant.orgUnits.field.name')}
            required
            autoFocus
            value={form.name}
            onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
            data-testid="org-unit-field-name"
          />
          <Input
            label={t('tenant.orgUnits.field.code')}
            value={form.code}
            onChange={(e) => setForm((s) => ({ ...s, code: e.target.value }))}
            maxLength={20}
            data-testid="org-unit-field-code"
          />
          <FormField label={t('tenant.orgUnits.field.kind')}>
            <Dropdown
              value={form.kind}
              onChange={(v) => setForm((s) => ({ ...s, kind: v as OrgUnitKind }))}
              data-testid="org-unit-field-kind"
              options={ORG_UNIT_KINDS.map((k) => ({
                value: k,
                label: t(`tenant.settings.orgStructure.kind.${k}`),
              }))}
            />
          </FormField>
          <FormField label={t('tenant.orgUnits.field.manager')}>
            <Dropdown
              value={form.managerEmployeeId}
              onChange={(v) => setForm((s) => ({ ...s, managerEmployeeId: v }))}
              data-testid="org-unit-field-manager"
              options={[
                { value: '', label: t('common.notSet') },
                ...managers.map((m) => ({ value: m.id, label: `${m.lastName} ${m.firstName}` })),
              ]}
            />
          </FormField>
          {modal?.kind === 'edit' && (
            <FormField label={t('tenant.orgUnits.field.parent')}>
              <Dropdown
                value={form.parentId}
                onChange={(v) => setForm((s) => ({ ...s, parentId: v }))}
                data-testid="org-unit-field-parent"
                options={[
                  { value: '', label: t('tenant.orgUnits.field.parentNone') },
                  ...parentPickerOptions,
                ]}
              />
            </FormField>
          )}
          <Checkbox
            label={t('tenant.orgUnits.field.isActive')}
            checked={form.isActive}
            onChange={(e) => setForm((s) => ({ ...s, isActive: e.target.checked }))}
            data-testid="org-unit-field-isActive"
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        variant="danger"
        title={t('common.confirmDelete')}
        message={t('tenant.orgUnits.deleteConfirm')}
        onConfirm={() => {
          const id = confirmDeleteId;
          setConfirmDeleteId(null);
          if (id) deleteMutation.mutate(id);
        }}
        onClose={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}

function OrgUnitNode({
  unit,
  depth,
  unitsByParent,
  collapsed,
  onToggleCollapsed,
  onAddChild,
  onEdit,
  onDelete,
  onMove,
  pendingDeleteId,
  index,
  siblingsLength,
}: {
  unit: OrgUnitItem;
  depth: number;
  unitsByParent: Map<string | null, OrgUnitItem[]>;
  collapsed: Set<string>;
  onToggleCollapsed: (id: string) => void;
  onAddChild: (parentId: string) => void;
  onEdit: (unit: OrgUnitItem) => void;
  onDelete: (id: string) => void;
  onMove: (unit: OrgUnitItem, direction: -1 | 1) => void;
  pendingDeleteId: string | null;
  index: number;
  siblingsLength: number;
}) {
  const { t } = useTranslation();
  const children = unitsByParent.get(unit.id) ?? [];
  const isCollapsed = collapsed.has(unit.id);

  return (
    <li>
      <div
        className="flex items-center gap-1.5 rounded px-1.5 py-1 text-sm hover:bg-bg-secondary"
        style={{ paddingLeft: depth * 20 }}
        data-testid={`org-unit-row-${unit.id}`}
      >
        {children.length > 0 ? (
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={isCollapsed ? t('tenant.orgUnits.expand') : t('tenant.orgUnits.collapse')}
            onClick={() => onToggleCollapsed(unit.id)}
            data-testid={`org-unit-toggle-${unit.id}`}
          >
            {isCollapsed ? <ChevronRight aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
          </IconButton>
        ) : (
          <span className="inline-block h-7 w-7 flex-none" aria-hidden="true" />
        )}

        <span
          className={`min-w-0 flex-1 truncate ${unit.isActive ? 'text-text-primary' : 'text-text-tertiary line-through'}`}
        >
          {unit.name}
        </span>
        {unit.code && <Badge color="gray">{unit.code}</Badge>}
        <Badge tone={KIND_BADGE_TONE[unit.kind]}>
          {t(`tenant.settings.orgStructure.kind.${unit.kind}`)}
        </Badge>
        {unit.managerName && (
          <span className="hidden truncate text-xs text-text-tertiary sm:inline">
            {unit.managerName}
          </span>
        )}

        <IconButton
          variant="ghost"
          size="sm"
          aria-label={t('common.moveUp')}
          disabled={index <= 0}
          onClick={() => onMove(unit, -1)}
          data-testid={`org-unit-move-up-${unit.id}`}
        >
          <MoveUp aria-hidden="true" />
        </IconButton>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={t('common.moveDown')}
          disabled={index >= siblingsLength - 1}
          onClick={() => onMove(unit, 1)}
          data-testid={`org-unit-move-down-${unit.id}`}
        >
          <MoveDown aria-hidden="true" />
        </IconButton>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={t('tenant.orgUnits.addSubBtn')}
          onClick={() => onAddChild(unit.id)}
          data-testid={`org-unit-add-sub-${unit.id}`}
        >
          <Plus aria-hidden="true" />
        </IconButton>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={t('common.edit')}
          onClick={() => onEdit(unit)}
          data-testid={`org-unit-edit-${unit.id}`}
        >
          <Pencil aria-hidden="true" />
        </IconButton>
        <IconButton
          variant="danger"
          size="sm"
          aria-label={t('common.delete')}
          disabled={pendingDeleteId === unit.id}
          onClick={() => onDelete(unit.id)}
          data-testid={`org-unit-delete-${unit.id}`}
        >
          <Trash2 aria-hidden="true" />
        </IconButton>
      </div>

      {!isCollapsed && children.length > 0 && (
        <ul className="space-y-0.5">
          {children.map((child, childIdx) => (
            <OrgUnitNode
              key={child.id}
              unit={child}
              depth={depth + 1}
              unitsByParent={unitsByParent}
              collapsed={collapsed}
              onToggleCollapsed={onToggleCollapsed}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              onMove={onMove}
              pendingDeleteId={pendingDeleteId}
              index={childIdx}
              siblingsLength={children.length}
            />
          ))}
        </ul>
      )}
    </li>
  );
}
